import type { Filter } from '~/features/search/types'
import type { FiltersCombineMode, Sort } from '~/types'
import type { SearchQuery } from '@rushdb/javascript-sdk'

export const queryKeys = {
  settings: () => ['settings'] as const,

  health: () => ['health'] as const,

  user: () => ['user'] as const,

  workspaces: {
    all: () => ['workspaces'] as const,
    detail: (id: string) => ['workspaces', id] as const,
    users: (id: string) => ['workspaces', id, 'users'] as const,
    accessList: (id: string) => ['workspaces', id, 'access-list'] as const,
    pendingInvites: (id: string) => ['workspaces', id, 'pending-invites'] as const,
    projects: (id: string) => ['workspaces', id, 'projects'] as const,
    tokens: (id: string) => ['workspaces', id, 'tokens'] as const,
    sso: (id: string) => ['workspaces', id, 'sso'] as const
  },

  projects: {
    detail: (projectId: string) => ['projects', projectId] as const,
    tokens: (projectId: string) => ['projects', projectId, 'tokens'] as const,
    labels: (projectId: string) => ['projects', projectId, 'labels'] as const,
    indexStats: (projectId: string, indexId: string) =>
      ['projects', projectId, 'indexes', indexId, 'stats'] as const,
    semanticSearchTest: (projectId: string, indexId: string) =>
      ['projects', projectId, 'indexes', indexId, 'semantic-search-test'] as const,
    fields: (
      projectId: string,
      params: {
        labels: string[]
        combineMode: FiltersCombineMode
        filters: Filter[]
        searchQuery?: SearchQuery
      }
    ) => ['projects', projectId, 'fields', params] as const,
    suggestedFields: (projectId: string, params: { labels: string[]; filters: Filter[] }) =>
      ['projects', projectId, 'suggested-fields', params] as const,
    indexes: (projectId: string) => ['projects', projectId, 'indexes'] as const,
    textIndexes: (projectId: string) => ['projects', projectId, 'text-indexes'] as const,
    connectors: (projectId: string) => ['projects', projectId, 'connectors'] as const,
    savedQueries: (projectId: string) => ['projects', projectId, 'saved-queries'] as const,
    schema: (projectId: string) => ['projects', projectId, 'schema'] as const,
    schemaMarkdown: (projectId: string) => ['projects', projectId, 'schema-md'] as const,
    relationshipPatterns: (projectId: string) => ['projects', projectId, 'relationship-patterns'] as const,
    stats: (projectId: string) => ['projects', projectId, 'stats'] as const,
    records: (
      projectId: string,
      params: {
        filters: Filter[]
        orderBy: Sort | undefined
        skip: number
        limit: number
        labels: string[]
        combineMode: FiltersCombineMode
        searchMode?: string
        searchQuery?: SearchQuery
        semanticSearch?: unknown
        keywordSearch?: unknown
      }
    ) => ['projects', projectId, 'records', params] as const,
    recordRelations: (
      projectId: string,
      params: {
        filters: Filter[]
        orderBy: Sort | undefined
        skip: number
        limit: number
        labels: string[]
        combineMode: FiltersCombineMode
      }
    ) => ['projects', projectId, 'record-relations', params] as const,
    graphRecordRelations: (projectId: string, recordIds: string[]) =>
      ['projects', projectId, 'graph-record-relations', recordIds] as const,
    record: (recordId: string) => ['records', recordId] as const,
    recordFields: (recordId: string) => ['records', recordId, 'fields'] as const,
    recordRelated: (recordId: string) => ['records', recordId, 'related'] as const
  },

  billing: {
    catalog: () => ['billing', 'catalog'] as const,
    state: (workspaceId: string) => ['billing', 'state', workspaceId] as const
  }
}
